import React, { FC, useCallback, useContext, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import { useGoogleLogin } from '@react-oauth/google';
import { useTranslation } from 'react-i18next';
import PageWrapper from '../../layout/PageWrapper/PageWrapper';
import Page from '../../layout/Page/Page';
import Card, { CardBody } from '../../components/bootstrap/Card';
import FormGroup from '../../components/bootstrap/forms/FormGroup';
import Input from '../../components/bootstrap/forms/Input';
import Checks from '../../components/bootstrap/forms/Checks';
import Button from '../../components/bootstrap/Button';
import AuthContext from '../../contexts/authContext';
import Spinner from '../../components/bootstrap/Spinner';
import Icon from '../../components/icon/Icon';
import Banner from '../../assets/img/website/logo-header-edge.png';
import { telemetryEvents } from '../../telemetry';
import useDarkMode from '../../hooks/useDarkMode';

/* Google publishes an official neutral button style for each theme (Identity
 * Services branding guidelines) — using those exact colors, rather than the
 * site's own dark-mode palette, keeps the button both on-theme and correctly
 * contrasted (an arbitrary dark fill/text pairing here risks low-contrast
 * dark-on-dark). */
const getGoogleButtonStyle = (dark: boolean): React.CSSProperties => ({
	borderRadius: 12,
	padding: '12px 0',
	fontWeight: 600,
	fontSize: 14,
	fontFamily: "'Quicksand', sans-serif",
	backgroundColor: dark ? '#131314' : '#fff',
	color: dark ? '#e3e3e3' : '#3c4043',
	border: dark ? '1px solid #8e918f' : '1px solid #dadce0',
});

interface IGoogleSignInButtonProps {
	isLoading: boolean;
	dark: boolean;
	onSuccess: (accessToken: string) => void;
	onError: () => void;
}

/**
 * Isolated so useGoogleLogin's mount-time initTokenClient() call only ever
 * runs when a client ID is actually configured — with a blank client_id it
 * throws synchronously during a passive effect, which (uncaught) blanks the
 * whole app since there's no boundary that can intercept an effect that deep.
 */
const GoogleSignInButton: FC<IGoogleSignInButtonProps> = ({ isLoading, dark, onSuccess, onError }) => {
	const { t } = useTranslation();
	const googleLogin = useGoogleLogin({
		onSuccess: (tokenResponse) => onSuccess(tokenResponse.access_token),
		onError,
	});

	return (
		<Button
			className='w-100'
			icon='CustomGoogle'
			isDisable={isLoading}
			style={getGoogleButtonStyle(dark)}
			onClick={() => googleLogin()}>
			{t('Continue with Google')}
		</Button>
	);
};

interface ILoginProps {
	isSignUp?: boolean;
}
const Login: FC<ILoginProps> = ({ isSignUp }) => {
	const { t } = useTranslation();
	const { setUser } = useContext(AuthContext);
	const { darkModeStatus } = useDarkMode();

	const navigate = useNavigate();
	const location = useLocation();
	// handleAuthError (e.g. an expired session on My Shelf) redirects here with
	// ?returnUrl=..., not router state — check both so re-login sends people back.
	const returnUrl = (location.state as { returnUrl?: string })?.returnUrl
		|| new URLSearchParams(location.search).get('returnUrl')
		|| '/';
	const handleOnClick = useCallback(() => navigate(returnUrl), [navigate, returnUrl]);

	const serverUrl = process.env.REACT_APP_SERVER_URL;
	const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [singUpStatus, setSingUpStatus] = useState<boolean>(!!isSignUp);
	const [errorMessage, setErrorMessage] = useState<string>('');

	const passwordCheck = async (username: string, password: string) => {
		setIsLoading(true);
		setErrorMessage('');
		const data = {
			'email': username,
			'password': password
		}

		const response = await fetch(`${serverUrl}/api/auth/login`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify(data)
		});

		if (response.status === 200) {
			const data = await response.json();
			if (data) {
				localStorage.setItem('token', data.token);
				localStorage.setItem('user', data.user);
				if (setUser) setUser(data.user);
				setIsLoading(false);
				return true;
			}
		}

		setIsLoading(false);
		return false;
	};

	const signUp = async (username: string, password: string, email: string, consent: boolean) => {
		setIsLoading(true);
		setErrorMessage('');
		const data = {
			'email': email,
			'password': password,
			'name': username,
			'consentGiven': consent,
		}

		const response = await fetch(`${serverUrl}/api/auth/register`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify(data)
		});

		if (response.status === 200) {
			const data = await response.json();
			if (data) {
				localStorage.setItem('token', data.token);
				localStorage.setItem('user', data.user);
				if (setUser) setUser(data.user);
				setIsLoading(false);
				return true;
			}
		}

		setIsLoading(false);
		return false;
	};

	const googleAuth = async (accessToken: string) => {
		setIsLoading(true);
		setErrorMessage('');

		const response = await fetch(`${serverUrl}/api/auth/google`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify({ accessToken })
		});

		if (response.status === 200) {
			const data = await response.json();
			if (data) {
				localStorage.setItem('token', data.token);
				localStorage.setItem('user', data.user);
				if (setUser) setUser(data.user);
				setIsLoading(false);
				return true;
			}
		}

		setIsLoading(false);
		return false;
	};

	const handleGoogleSuccess = (accessToken: string) => {
		googleAuth(accessToken).then((res) => {
			if (res) {
				telemetryEvents.userSignedIn();
				handleOnClick();
			} else {
				setErrorMessage(t('Could not sign in with Google. Please try again.'));
			}
		});
	};

	const handleGoogleError = () => setErrorMessage(t('Could not sign in with Google. Please try again.'));

	const formik = useFormik({
		initialValues: {
			loginUsername: '',
			loginPassword: '',
		},
		validate: (values) => {
			const errors: { loginUsername?: string; loginPassword?: string } = {};

			if (!values.loginUsername) {
				errors.loginUsername = t('Required');
			}

			if (!values.loginPassword) {
				errors.loginPassword = t('Required');
			}

			return errors;
		},
		validateOnChange: false,
		onSubmit: (values) => {
			passwordCheck(values.loginUsername, values.loginPassword).then((res) => {
				if (res) {
					telemetryEvents.userSignedIn();
					handleOnClick();
				} else {
					setErrorMessage(t('Email or password is incorrect.'));
				}
			});
		},
	});

	const formikSignup = useFormik({
		initialValues: {
			signUpUsername: '',
			signUpPassword: '',
			signUpEmail: '',
			consentGiven: false,
		},
		validate: (values) => {
			const errors: { signUpUsername?: string; signUpPassword?: string; signUpEmail?: string; consentGiven?: string } = {};

			if (!values.signUpUsername) {
				errors.signUpUsername = t('Required');
			}

			if (!values.signUpPassword) {
				errors.signUpPassword = t('Required');
			}

			if (!values.signUpEmail) {
				errors.signUpEmail = t('Required');
			}

			if (!values.consentGiven) {
				errors.consentGiven = t('You must accept the privacy policy and terms to create an account');
			}

			return errors;
		},
		validateOnChange: false,
		onSubmit: (values) => {
			signUp(values.signUpUsername, values.signUpPassword, values.signUpEmail, values.consentGiven).then((res) => {
				if (res) {
					telemetryEvents.userSignedUp();
					handleOnClick();
				} else {
					setErrorMessage(t('Could not create account. Please try again.'));
				}
			});
		},
	});

	/* ── Shared chip-style tab toggle ── */
	const tabStyle = (active: boolean): React.CSSProperties => ({
		flex: 1,
		padding: '10px 0',
		borderRadius: 12,
		border: 'none',
		fontFamily: "'Quicksand', sans-serif",
		fontWeight: 600,
		fontSize: 15,
		cursor: 'pointer',
		transition: 'all 0.2s ease',
		backgroundColor: active ? '#4d69fa' : 'transparent',
		color: active ? '#fff' : '#6c757d',
	});

	return (
		<PageWrapper
			isProtected={false}
			seo={{ noindex: true }}
			title={singUpStatus ? t('Sign Up') : t('Login')}>
			<Page className='p-0'>
				<div className='row h-100 align-items-center justify-content-center'>
					<div className='col-xl-4 col-lg-5 col-md-7 col-11'>
						<Card style={{ borderRadius: 16, overflow: 'hidden' }} data-tour='login-page'>
							<CardBody style={{ padding: '40px 32px 32px' }}>
								{/* ── Logo ── */}
								<div className='text-center' style={{ marginBottom: 28 }}>
									<Link to='/' aria-label='StoriesVillage'>
										<img
											src={Banner}
											alt='StoriesVillage'
											style={{ width: 200, objectFit: 'contain' }}
										/>
									</Link>
								</div>

								{/* ── Tab toggle ── */}
								<div
									style={{
										display: 'flex',
										gap: 6,
										padding: 4,
										borderRadius: 14,
										backgroundColor: '#f5f6fa',
										marginBottom: 24,
									}}>
									<button
										type='button'
										style={tabStyle(!singUpStatus)}
										onClick={() => {
											setSingUpStatus(false);
											setErrorMessage('');
										}}>
										{t('Login')}
									</button>
									<button
										type='button'
										style={tabStyle(singUpStatus)}
										onClick={() => {
											setSingUpStatus(true);
											setErrorMessage('');
										}}>
										{t('Sign Up')}
									</button>
								</div>

								{/* ── Heading ── */}
								<div style={{ marginBottom: 24 }}>
									<h3
										style={{
											fontFamily: "'Quicksand', sans-serif",
											fontWeight: 700,
											marginBottom: 4,
										}}>
										{singUpStatus ? t('Create your account') : t('Welcome back!')}
									</h3>
									<p className='text-muted' style={{ fontSize: 14, margin: 0 }}>
										{singUpStatus
											? t('Sign up to get started!')
											: t('Sign in to continue!')}
									</p>
								</div>

								{/* ── Error message ── */}
								{errorMessage && (
									<div
										style={{
											backgroundColor: '#fef2f2',
											color: '#dc3545',
											borderRadius: 10,
											padding: '10px 14px',
											fontSize: 13,
											fontWeight: 500,
											marginBottom: 16,
											display: 'flex',
											alignItems: 'center',
											gap: 8,
										}}>
										<Icon icon='Error' style={{ fontSize: 18 }} />
										{errorMessage}
									</div>
								)}

								{/* ── Sign Up form ── */}
								{singUpStatus ? (
									<form
										className='d-flex flex-column'
										style={{ gap: 14 }}
										noValidate
										onSubmit={formikSignup.handleSubmit}>
										<FormGroup
											id='signUpEmail'
											isFloating
											label={t('Your email')}>
											<Input
												type='email'
												autoComplete='email'
												value={formikSignup.values.signUpEmail}
												isTouched={formikSignup.touched.signUpEmail}
												invalidFeedback={formikSignup.errors.signUpEmail}
												isValid={formikSignup.isValid}
												onChange={formikSignup.handleChange}
												onBlur={formikSignup.handleBlur}
												onFocus={() => {
													formikSignup.setErrors({});
													setErrorMessage('');
												}}
											/>
										</FormGroup>
										<FormGroup
											id='signUpUsername'
											isFloating
											label={t('Your name')}>
											<Input
												autoComplete='given-name'
												value={formikSignup.values.signUpUsername}
												isTouched={formikSignup.touched.signUpUsername}
												invalidFeedback={formikSignup.errors.signUpUsername}
												isValid={formikSignup.isValid}
												onChange={formikSignup.handleChange}
												onBlur={formikSignup.handleBlur}
												onFocus={() => {
													formikSignup.setErrors({});
													setErrorMessage('');
												}}
											/>
										</FormGroup>
										<FormGroup
											id='signUpPassword'
											isFloating
											label={t('Password')}>
											<Input
												type='password'
												autoComplete='new-password'
												value={formikSignup.values.signUpPassword}
												isTouched={formikSignup.touched.signUpPassword}
												invalidFeedback={formikSignup.errors.signUpPassword}
												isValid={formikSignup.isValid}
												onChange={formikSignup.handleChange}
												onBlur={formikSignup.handleBlur}
												onFocus={() => {
													formikSignup.setErrors({});
													setErrorMessage('');
												}}
											/>
										</FormGroup>
										<div style={{ marginTop: 4 }}>
											<Checks
												id='consentGiven'
												name='consentGiven'
												label={
													<span style={{ fontSize: 13 }}>
														{t('I agree to the')}{' '}
														<a href='/privacy' target='_blank' rel='noopener noreferrer' style={{ color: '#4d69fa', textDecoration: 'underline' }}>
															{t('Privacy Policy')}
														</a>
														{' '}{t('and')}{' '}
														<a href='/terms' target='_blank' rel='noopener noreferrer' style={{ color: '#4d69fa', textDecoration: 'underline' }}>
															{t('Terms of Service')}
														</a>
													</span>
												}
												checked={formikSignup.values.consentGiven}
												onChange={formikSignup.handleChange}
												isTouched={formikSignup.touched.consentGiven}
												isValid={formikSignup.isValid}
												invalidFeedback={formikSignup.errors.consentGiven}
											/>
										</div>
										<Button
											type='submit'
											color='primary'
											className='w-100'
											isDisable={isLoading}
											style={{
												borderRadius: 12,
												padding: '14px 0',
												fontWeight: 600,
												fontSize: 15,
												fontFamily: "'Quicksand', sans-serif",
												marginTop: 4,
											}}>
											{isLoading && <Spinner isSmall inButton isGrow />}
											{t('Sign Up')}
										</Button>
									</form>
								) : (
									/* ── Login form ── */
									<form
										className='d-flex flex-column'
										style={{ gap: 14 }}
										noValidate
										onSubmit={formik.handleSubmit}>
										<FormGroup
											id='loginUsername'
											isFloating
											label={t('Your email')}>
											<Input
												autoComplete='username'
												value={formik.values.loginUsername}
												isTouched={formik.touched.loginUsername}
												invalidFeedback={formik.errors.loginUsername}
												isValid={formik.isValid}
												onChange={formik.handleChange}
												onBlur={formik.handleBlur}
												onFocus={() => {
													formik.setErrors({});
													setErrorMessage('');
												}}
											/>
										</FormGroup>
										<FormGroup
											id='loginPassword'
											isFloating
											label={t('Password')}>
											<Input
												type='password'
												autoComplete='current-password'
												value={formik.values.loginPassword}
												isTouched={formik.touched.loginPassword}
												invalidFeedback={formik.errors.loginPassword}
												isValid={formik.isValid}
												onChange={formik.handleChange}
												onBlur={formik.handleBlur}
												onFocus={() => {
													formik.setErrors({});
													setErrorMessage('');
												}}
											/>
										</FormGroup>
										<div className='text-end' style={{ marginTop: -6 }}>
											<Link
												to='/forgot-password'
												style={{ color: '#4d69fa', fontWeight: 600, fontSize: 13 }}>
												{t('Forgot password?')}
											</Link>
										</div>
										<Button
											type='submit'
											color='primary'
											className='w-100'
											isDisable={isLoading}
											style={{
												borderRadius: 12,
												padding: '14px 0',
												fontWeight: 600,
												fontSize: 15,
												fontFamily: "'Quicksand', sans-serif",
												marginTop: 4,
											}}>
											{isLoading && <Spinner isSmall inButton isGrow />}
											{t('Login')}
										</Button>
									</form>
								)}

								{/* ── Divider ── */}
								<div
									className='d-flex align-items-center'
									style={{ margin: '20px 0', gap: 12 }}>
									<div style={{ flex: 1, height: 1, backgroundColor: '#e0e0e0' }} />
									<span className='text-muted' style={{ fontSize: 13, fontWeight: 500 }}>
										{t('OR')}
									</span>
									<div style={{ flex: 1, height: 1, backgroundColor: '#e0e0e0' }} />
								</div>

								{/* ── Social login ── */}
								{googleClientId ? (
									<GoogleSignInButton
										isLoading={isLoading}
										dark={darkModeStatus}
										onSuccess={handleGoogleSuccess}
										onError={handleGoogleError}
									/>
								) : (
									<Button
										className='w-100'
										icon='CustomGoogle'
										isDisable
										title={t('Google sign-in is not configured yet')}
										style={getGoogleButtonStyle(darkModeStatus)}>
										{t('Continue with Google')}
									</Button>
								)}
								<p
									className='text-muted text-center'
									style={{ fontSize: 11, margin: '10px 0 0' }}>
									{t('By continuing with Google, you agree to our')}{' '}
									<a href='/privacy' target='_blank' rel='noopener noreferrer' style={{ color: '#4d69fa' }}>
										{t('Privacy Policy')}
									</a>
									{' '}{t('and')}{' '}
									<a href='/terms' target='_blank' rel='noopener noreferrer' style={{ color: '#4d69fa' }}>
										{t('Terms of Service')}
									</a>
									.
								</p>

								{/* ── Footer links ── */}
								<div
									className='text-center text-muted'
									style={{ marginTop: 24, fontSize: 12 }}>
									<a
										href='/privacy'
										className='text-decoration-none text-muted'
										style={{ marginRight: 16 }}>
										{t('Privacy policy')}
									</a>
									<a
										href='/'
										className='text-decoration-none text-muted'>
										{t('Terms of use')}
									</a>
								</div>
							</CardBody>
						</Card>
					</div>
				</div>
			</Page>
		</PageWrapper>
	);
};

export default Login;
